import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { cn, tailwindUtils } from '@/shared/ui/utils/tailwind';
import HousienBrandLogo from '@/shared/ui/components/basic/logos/HousienBrandLogo';
import { PublicVistoryService, type VistoryReport } from '@/features/vistories/services/public-vistory-service';
import { VistoryCodeEntry } from './VistoryCodeEntry';
import { VistoryReportView } from './VistoryReportView';
import { VistoryConfirmation } from './VistoryConfirmation';

type Step = 'code' | 'report' | 'done';

/**
 * Public, token-protected page for a tenant to review and approve/reject a vistory.
 *
 * Walks through three steps: entering the access code, viewing the report, and
 * a final confirmation. Rendered outside the authenticated app shell.
 */
export function PublicVistoryApprovalPage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation('features/vistories');
  const [step, setStep] = useState<Step>('code');
  const [code, setCode] = useState('');
  const [report, setReport] = useState<VistoryReport | null>(null);
  const [outcome, setOutcome] = useState<'approved' | 'rejected'>('approved');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    dismissSplash();
  }, []);

  async function handleVerified(verifiedCode: string) {
    setCode(verifiedCode);
    if (!token) return;
    const res = await PublicVistoryService.getReport(token, verifiedCode);
    if (res.ok && res.data) {
      setReport(res.data);
      setStep('report');
    }
  }

  async function handleApprove() {
    if (!token) return;
    setActionLoading(true);
    try {
      const res = await PublicVistoryService.approve(token, code);
      if (res.ok) { setOutcome('approved'); setStep('done'); }
    } finally {
      setActionLoading(false);
    }
  }

  async function handleReject(reason: string, appointments: Array<{ date: string; time: string; note?: string }>) {
    if (!token) return;
    setActionLoading(true);
    try {
      const res = await PublicVistoryService.reject(token, code, reason, appointments);
      if (res.ok) { setOutcome('rejected'); setStep('done'); }
    } finally {
      setActionLoading(false);
    }
  }

  if (!token) return null;

  return (
    <div className="min-h-screen bg-[var(--surface-secondary)] [font-family:var(--font-family)]">
      <header className="flex items-center justify-center border-b border-[var(--border-subtle)] bg-[var(--surface-primary)] px-[var(--space-4)] py-[var(--space-3)]">
        <HousienBrandLogo variant="full" height={40} />
      </header>

      <main className="mx-auto max-w-xl px-[var(--space-4)] py-[var(--space-8)]">
        <div className={cn(
          tailwindUtils.card,
          'p-[var(--space-6)] shadow-[var(--shadow-md)]',
        )}>
          {step === 'code' && (
            <VistoryCodeEntry token={token} onVerified={handleVerified} />
          )}
          {step === 'report' && report && (
            <VistoryReportView
              report={report}
              onApprove={handleApprove}
              onReject={handleReject}
              loading={actionLoading}
            />
          )}
          {step === 'done' && <VistoryConfirmation outcome={outcome} />}
        </div>
      </main>
    </div>
  );
}

/**
 * Hides the initial HTML splash screen and restores document scrolling.
 *
 * Reverses the scroll-lock styles applied by the pre-React boot splash so the
 * page becomes interactive once the approval flow has mounted.
 */
function dismissSplash() {
  const splash = document.getElementById('app-splash');
  if (splash) {
    splash.style.opacity = '0';
    setTimeout(() => splash.remove(), 200);
  }
  document.body.style.overflow = 'auto';
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.width = '';
  document.body.style.height = 'auto';
  document.body.style.paddingTop = '0';
  document.body.style.paddingBottom = '0';
  document.documentElement.style.overflow = 'auto';
  document.documentElement.style.height = 'auto';
  const root = document.getElementById('root');
  if (root) { root.style.overflow = 'auto'; root.style.height = 'auto'; }
}
